import React from 'react';
import { AutoField } from '@puckeditor/core';
import { THEMED_BLOCKS } from '../themedBlocks';
import { useSelectedItemProps } from './useSelectedItemProps';
import StyleDefaultMark from './StyleDefaultMark';

/**
 * Pour un champ composite, qui règle plusieurs props : celles que le style du
 * thème impose au bloc sélectionné, à masquer tant qu'il le suit (pendant de
 * `themedBlockResolveFields`, qui ne voit que les champs entiers).
 */
export function useThemeLockedProp(kind) {
  const { itemProps } = useSelectedItemProps();
  const linked = itemProps?.themeStyleId != null;
  const { lockedProps } = THEMED_BLOCKS[kind];
  return (key) => linked && lockedProps.includes(key);
}

/** Le champ, avec la valeur que le bloc affiche (celle du style qu'il suit). */
function ThemedValueField({
  name, field, value, onChange, ...rest
}) {
  const { itemProps } = useSelectedItemProps();
  const shown = itemProps.themeStyleId != null && name in itemProps ? itemProps[name] : value;
  return (
    <StyleDefaultMark propKey={name}>
      {field.type === 'custom'
        ? field.render({
          ...rest, name, field, value: shown, onChange,
        })
        : <AutoField field={field} value={shown} onChange={onChange} />}
    </StyleDefaultMark>
  );
}

/**
 * Champs d'un bloc thémable dont les réglages de style montrent la valeur
 * rendue sur le canvas : celle du style du thème que le bloc suit, retouches
 * du thème comprises. Puck remplit les champs avec les props enregistrées
 * (la copie faite au lien) ; le canvas, lui, lit le style à chaque rendu.
 */
export function withThemedFieldValues(kind, fields) {
  const { styleProps } = THEMED_BLOCKS[kind];
  return Object.fromEntries(Object.entries(fields).map(([name, field]) => {
    if (!styleProps.includes(name)) return [name, field];
    // La forme d'origine reste lisible (options, libellé) : seul le rendu passe
    // par la valeur affichée.
    return [name, {
      ...field,
      type: 'custom',
      render: (props) => <ThemedValueField {...props} name={name} field={field} />,
    }];
  }));
}
